import React, { useEffect, useMemo, useState } from 'react';
import api from './api';
import TaskModal from './TaskModal';
import {
  STATUS_LABELS,
  STATUS_BADGE_CLASS,
  PRIORITY_BADGE_CLASS,
  PRIORITY_LABELS,
  userDisplayName,
  taskCode,
  formatDate,
  isOverdue,
  libelleBlocage,
  joursDeBlocage,
  niveauEscalade,
} from './helpers';

function MyTasks({ users, currentUser, onOpenProject }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openTask, setOpenTask] = useState(undefined);
  const [personFilter, setPersonFilter] = useState(currentUser.id);
  // 'personne' = les tâches d'une personne ; 'equipe' = la charge de tous.
  const [vue, setVue] = useState('personne');

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/tasks'), api.get('/projects')])
      .then(([tasksRes, projectsRes]) => {
        // Les projets archivés ne sont plus renvoyés : leurs tâches sortent
        // aussi de « Mes tâches ».
        const visibles = new Set(projectsRes.data.map((p) => p.id));
        // Les jalons ne sont du travail pour personne : ils n'ont pas leur
        // place dans « Mes tâches » ni dans le calcul de la charge.
        setTasks(
          tasksRes.data.filter((t) => visibles.has(t.project_id) && !t.est_jalon)
        );
        setProjects(projectsRes.data);
      })
      .catch(() => setError('Impossible de charger les tâches.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const idFiltre = parseInt(personFilter);

  // Une personne voit les tâches dont elle est responsable, celles qui lui ont
  // été déléguées, et celles dont elle assure l'intérim pendant une rotation —
  // sinon ces dernières n'apparaîtraient chez personne pendant dix jours.
  const myTasks = tasks.filter(
    (t) =>
      (t.assigned_to === idFiltre || t.delegated_to === idFiltre || t.interim_id === idFiltre) &&
      t.status !== 'Done'
  );

  // Charge de toute l'équipe, tous projets confondus. C'est le seul écran qui
  // montre qu'une personne porte trente tâches pendant qu'une autre en a deux.
  const charge = useMemo(() => {
    const ouvertes = tasks.filter((t) => t.status !== 'Done');
    return users
      .map((u) => {
        const siennes = ouvertes.filter((t) => t.assigned_to === u.id || t.delegated_to === u.id);
        return {
          user: u,
          total: siennes.length,
          projets: new Set(siennes.map((t) => t.project_id)).size,
          retard: siennes.filter(isOverdue).length,
          bloquees: siennes.filter((t) => t.status === 'Blocked').length,
          interim: ouvertes.filter((t) => t.interim_id === u.id).length,
        };
      })
      .filter((l) => l.total > 0 || l.interim > 0)
      .sort((a, b) => b.total - a.total);
  }, [tasks, users]);

  const chargeMax = Math.max(1, ...charge.map((l) => l.total));

  const projectName = (id) => projects.find((p) => p.id === id)?.name || `Projet #${id}`;
  const projectOf = (task) => projects.find((p) => p.id === task.project_id);
  const nomDe = (id) => userDisplayName(users.find((u) => u.id === id));

  const handleTaskSaved = () => {
    setOpenTask(undefined);
    load();
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <div className="view-toggle">
          <button
            className={`view-toggle-btn ${vue === 'personne' ? 'active' : ''}`}
            onClick={() => setVue('personne')}
          >
            Par personne
          </button>
          <button
            className={`view-toggle-btn ${vue === 'equipe' ? 'active' : ''}`}
            onClick={() => setVue('equipe')}
          >
            Charge de l’équipe
          </button>
        </div>
      </div>

      {vue === 'equipe' && (
        <>
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Toutes les tâches ouvertes, tous projets confondus. Cliquez sur une ligne
            pour voir le détail des tâches de la personne.
          </p>
          <div className="table-container">
            <table className="rapport-tableau">
              <thead>
                <tr>
                  <th>Personne</th>
                  <th>Charge</th>
                  <th className="rapport-col-droite">Tâches ouvertes</th>
                  <th className="rapport-col-droite">Projets</th>
                  <th className="rapport-col-droite">En retard</th>
                  <th className="rapport-col-droite">Bloquées</th>
                  <th className="rapport-col-droite">Intérim</th>
                </tr>
              </thead>
              <tbody>
                {charge.map((l) => (
                  <tr
                    key={l.user.id}
                    className="clickable"
                    onClick={() => { setPersonFilter(l.user.id); setVue('personne'); }}
                  >
                    <td>
                      {userDisplayName(l.user)}
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{l.user.department}</div>
                    </td>
                    <td style={{ width: 180 }}>
                      <div className="rapport-jauge" style={{ margin: 0 }}>
                        <div
                          className="rapport-jauge-remplissage"
                          style={{
                            width: `${(l.total / chargeMax) * 100}%`,
                            background: l.retard > 0 ? 'var(--danger)' : 'var(--secondary)',
                          }}
                        />
                      </div>
                    </td>
                    <td className="rapport-col-droite" style={{ fontWeight: 600 }}>{l.total}</td>
                    <td className="rapport-col-droite">{l.projets}</td>
                    <td className={`rapport-col-droite ${l.retard ? 'rapport-retard' : ''}`}>
                      {l.retard || '—'}
                    </td>
                    <td className="rapport-col-droite">{l.bloquees || '—'}</td>
                    <td className="rapport-col-droite">{l.interim || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {charge.length === 0 && <div className="empty-state">Aucune tâche ouverte.</div>}
          </div>
        </>
      )}

      {vue === 'personne' && (
        <>
          {/* Une liste déroulante plutôt que vingt pastilles : on retrouve
              une personne par son nom et son service, sans balayer l'écran. */}
          <div className="page-header-row" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label htmlFor="mes-taches-personne" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Tâches de
              </label>
              <select
                id="mes-taches-personne"
                className="btn btn-sm"
                value={personFilter}
                onChange={(e) => setPersonFilter(e.target.value)}
              >
                {[...users]
                  .filter((u) => u.active !== false || u.id === idFiltre)
                  .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b), 'fr'))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {userDisplayName(u)}{u.id === currentUser.id ? ' (moi)' : ''}{u.department ? ` — ${u.department}` : ''}
                    </option>
                  ))}
              </select>
              {idFiltre !== currentUser.id && (
                <button type="button" className="lien-discret" onClick={() => setPersonFilter(currentUser.id)}>
                  Revenir à mes tâches
                </button>
              )}
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {myTasks.length} tâche{myTasks.length > 1 ? 's' : ''} ouverte{myTasks.length > 1 ? 's' : ''}
            </span>
          </div>

          {myTasks.length === 0 && (
            <div className="empty-state">
              <strong>Aucune tâche ouverte pour {idFiltre === currentUser.id ? 'vous' : nomDe(idFiltre)}.</strong>
              <div style={{ marginTop: 6 }}>
                Les tâches apparaissent ici dès qu'elles sont assignées, déléguées ou confiées en intérim depuis un projet.
              </div>
              <div className="empty-state-action">
                <button type="button" className="btn" onClick={() => setVue('equipe')}>Voir la charge de l’équipe</button>
              </div>
            </div>
          )}

          {myTasks.length > 0 && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Réf.</th>
                    <th>Tâche</th>
                    <th>Mon rôle</th>
                    <th>Projet</th>
                    <th>Statut</th>
                    <th>Priorité</th>
                    <th>Échéance</th>
                  </tr>
                </thead>
                <tbody>
                  {myTasks.map((task) => (
                    <tr key={task.id} className="clickable" onClick={() => setOpenTask(task)}>
                      <td>{taskCode(task.id)}</td>
                      <td>
                        {task.name}
                        {task.interim_name && task.assigned_to === idFiltre && (
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                            vous êtes absent — intérim : {task.interim_name}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {task.assigned_to === idFiltre ? (
                          <span className="badge badge-active">Responsable</span>
                        ) : task.delegated_to === idFiltre ? (
                          <span className="badge badge-review" title={`Responsable : ${nomDe(task.assigned_to)}`}>
                            Déléguée
                          </span>
                        ) : (
                          <span
                            className="badge badge-priority-medium"
                            title={`Titulaire absent : ${nomDe(task.assigned_to)}`}
                          >
                            Intérim
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          style={{ color: 'var(--secondary)', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const p = projectOf(task);
                            if (p) onOpenProject(p);
                          }}
                        >
                          {projectName(task.project_id)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${STATUS_BADGE_CLASS[task.status] || 'badge-todo'}`}>
                          {STATUS_LABELS[task.status] || task.status}
                        </span>
                        {task.status === 'Blocked' && (
                          <div
                            className={`blocage-ligne blocage-ligne-${niveauEscalade(joursDeBlocage(task)) || 'normal'}`}
                            title={task.blocage_motif || undefined}
                          >
                            {libelleBlocage(task)}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${PRIORITY_BADGE_CLASS[task.priority] || 'badge-priority-medium'}`}>
                          {PRIORITY_LABELS[task.priority] || task.priority}
                        </span>
                      </td>
                      <td>
                        <span className={isOverdue(task) ? 'date-retard' : ''}>{formatDate(task.due_date)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {openTask !== undefined && (
        <TaskModal
          task={openTask}
          projectId={openTask?.project_id}
          users={users}
          currentUser={currentUser}
          onClose={() => setOpenTask(undefined)}
          onSaved={handleTaskSaved}
          onDeleted={handleTaskSaved}
        />
      )}
    </div>
  );
}

export default MyTasks;
